class CartRecommendations extends HTMLElement {
  static responses = new Map();
  static positions = new Map();

  connectedCallback() {
    this.load().catch(() => {
      if (this.isConnected) this.remove();
    });
  }

  async load() {
    const productIds = [...new Set(this.dataset.productIds.split(',').filter(Boolean))].slice(0, 3);
    const cartProductIds = new Set(this.dataset.cartProductIds.split(',').filter(Boolean));
    const limit = Number(this.dataset.limit);
    this.cacheKey = `${this.dataset.productIds}:${this.dataset.cartProductIds}:${limit}`;
    const responses = await Promise.all(
      productIds.map((productId) => {
        const url = `${this.dataset.url}?section_id=cart-recommendations&product_id=${productId}&limit=${limit}&intent=complementary`;
        if (!CartRecommendations.responses.has(url)) {
          const request = fetch(url).then((response) => {
            if (!response.ok) throw new Error('Recommendations unavailable');
            return response.text();
          }).catch((error) => {
            CartRecommendations.responses.delete(url);
            throw error;
          });
          CartRecommendations.responses.set(url, request);
        }
        return CartRecommendations.responses.get(url);
      })
    );
    if (!this.isConnected) return;
    const products = new Map();

    responses.forEach((markup) => {
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      documentFragment.querySelectorAll('.cart-recommendations__card').forEach((card) => {
        if (!cartProductIds.has(card.dataset.productId) && !products.has(card.dataset.productId)) {
          products.set(card.dataset.productId, card);
        }
      });
    });

    const cards = [...products.values()].slice(0, limit);
    if (!cards.length) {
      this.remove();
      return;
    }

    const items = this.querySelector('.cart-recommendations__items');
    items.replaceChildren(...cards);
    items.removeAttribute('aria-busy');
    this.querySelector('.cart-recommendations__viewport').scrollLeft = CartRecommendations.positions.get(this.cacheKey) || 0;
    this.initializeNavigation(cards.length);
    this.querySelectorAll('[data-cart-recommendation-add]').forEach((button) => {
      button.addEventListener('click', () => this.add(button.closest('[data-variant-id]').dataset.variantId, button));
    });
  }

  initializeNavigation(total) {
    const viewport = this.querySelector('.cart-recommendations__viewport');
    const items = this.querySelector('.cart-recommendations__items');
    const previous = this.querySelector('[data-cart-recommendations-previous]');
    const next = this.querySelector('[data-cart-recommendations-next]');
    const update = () => {
      if (!this.isConnected) return;
      CartRecommendations.positions.set(this.cacheKey, viewport.scrollLeft);
      const card = items.firstElementChild;
      const gap = Number.parseFloat(getComputedStyle(items).gap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : 1;
      const current = Math.min(total, Math.max(1, Math.round(viewport.scrollLeft / step) + 1));
      this.querySelector('.cart-recommendations__counter').textContent = this.dataset.counterTemplate
        .replace('[current]', current)
        .replace('[total]', total);
      previous.disabled = current === 1;
      next.disabled = current === total;
    };
    const scroll = (direction) => {
      const card = items.firstElementChild;
      const gap = Number.parseFloat(getComputedStyle(items).gap) || 0;
      viewport.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    };

    previous.addEventListener('click', () => scroll(-1));
    next.addEventListener('click', () => scroll(1));
    viewport.addEventListener('scroll', update, { passive: true });
    update();
  }

  async add(variantId, button) {
    if (button.disabled) return;
    button.disabled = true;
    try {
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) {
        throw new Error(state.description || window.cartStrings.error);
      }
      document.querySelector('cart-drawer')?.renderContents(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations', cartData: state, variantId });
    } catch (error) {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
